<script setup>
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useBranding } from 'shared/composables/useBranding';
import { picoSearch } from '@scmmishra/pico-search';
import { useAccount } from 'dashboard/composables/useAccount';
import IntegrationItem from './IntegrationItem.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const { accountId } = useAccount();
const { replaceInstallationName } = useBranding();

const searchQuery = ref('');
const uiFlags = getters['integrations/getUIFlags'];

// Asaas, Pagar.me e Hotmart já têm casa no card "Compras"; os apps
// genéricos *_purchase abririam a página de hooks e duplicariam o gateway.
const PURCHASE_APP_SUFFIX = '_purchase';
const appIntegrations = computed(() =>
  (getters['integrations/getAppIntegrations'].value || []).filter(
    app => !String(app.id).endsWith(PURCHASE_APP_SUFFIX)
  )
);

const integrationList = computed(() => [
  {
    id: 'meta_connection',
    routeName: 'settings_integrations_meta',
    icon: 'i-ph-chat-circle-text',
    showStatus: false,
    name: t('INTEGRATION_SETTINGS.CATALOG.META_CONNECTION.NAME'),
    description: t('INTEGRATION_SETTINGS.CATALOG.META_CONNECTION.DESCRIPTION'),
  },
  {
    id: 'lead_capture',
    routeName: 'settings_integrations_lead_capture',
    icon: 'i-ph-link',
    showStatus: false,
    name: t('INTEGRATION_SETTINGS.CATALOG.LEAD_CAPTURE.NAME'),
    description: t('INTEGRATION_SETTINGS.CATALOG.LEAD_CAPTURE.DESCRIPTION'),
  },
  {
    id: 'meta_lead_forms',
    routeName: 'settings_integrations_meta_lead_forms',
    icon: 'i-ph-note-pencil',
    showStatus: false,
    name: t('INTEGRATION_SETTINGS.CATALOG.META_LEAD_FORMS.NAME'),
    description: t('INTEGRATION_SETTINGS.CATALOG.META_LEAD_FORMS.DESCRIPTION'),
  },
  {
    id: 'purchase_integrations',
    routeName: 'settings_integrations_purchases',
    icon: 'i-ph-shopping-cart',
    showStatus: false,
    name: t('INTEGRATION_SETTINGS.PURCHASE.CARD_TITLE'),
    description: t('INTEGRATION_SETTINGS.PURCHASE.CARD_DESCRIPTION'),
  },
  ...appIntegrations.value,
]);

const filteredIntegrationList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return integrationList.value;
  return picoSearch(integrationList.value, query, ['name', 'description']);
});

const fetchIntegrations = () => {
  store.dispatch('integrations/get');
};

onMounted(fetchIntegrations);

// The dashboard shell reuses this route when the account changes, so
// onMounted does not run again. Fetch the new account immediately so the
// previous account's apps are not left on screen.
watch(
  accountId,
  (next, prev) => {
    if (!next || !prev || String(next) === String(prev)) return;
    fetchIntegrations();
  },
  { flush: 'sync' }
);
</script>

<template>
  <SettingsLayout
    data-testid="page-integrations"
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('INTEGRATION_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INTEGRATION_SETTINGS.HEADER')"
        :description="
          replaceInstallationName($t('INTEGRATION_SETTINGS.DESCRIPTION'))
        "
        :link-text="$t('INTEGRATION_SETTINGS.LEARN_MORE')"
        :search-placeholder="$t('INTEGRATION_SETTINGS.SEARCH_PLACEHOLDER')"
        feature-name="integrations"
      />
    </template>
    <template #body>
      <div class="flex-grow flex-shrink overflow-auto">
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <!-- Integrações padrão -->
          <IntegrationItem
            v-for="item in filteredIntegrationList"
            :id="item.id"
            :key="item.id"
            :route-name="item.routeName"
            :icon="item.icon"
            :show-status="item.showStatus !== false"
            :name="item.name"
            :description="item.description"
            :enabled="item.enabled"
          />
        </div>
        <span
          v-if="!filteredIntegrationList.length && searchQuery"
          class="flex-1 flex items-center justify-center py-10 text-center text-body-main !text-base text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.NO_RESULTS') }}
        </span>
      </div>
    </template>
  </SettingsLayout>
</template>
